import { Box, Transition } from '@mantine/core';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export type FlipAxis = 'x' | 'y';

export interface FlipInProps {
  children: React.ReactNode;
  visible?: boolean;
  duration?: number;
  delay?: number;
  axis?: FlipAxis;
  degrees?: number;
  perspective?: number;
  timingFunction?: string;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited?: () => void;
}

const FlipIn = forwardRef<HTMLDivElement, FlipInProps>(
  (
    {
      children,
      visible = true,
      duration = 500,
      delay = 0,
      axis = 'y',
      degrees = 90,
      perspective = 1000,
      timingFunction = 'ease-out',
      onEnter,
      onEntered,
      onExit,
      onExited,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const actualDuration = reducedMotion ? 0 : duration;
    const rotateAxis = axis === 'x' ? 'rotateX' : 'rotateY';

    const flipTransition = {
      in: {
        opacity: 1,
        transform: `perspective(${perspective}px) ${rotateAxis}(0deg)`,
      },
      out: {
        opacity: 0,
        transform: `perspective(${perspective}px) ${rotateAxis}(${degrees}deg)`,
      },
      transitionProperty: 'opacity, transform',
    };

    return (
      <Transition
        mounted={visible}
        transition={flipTransition}
        duration={actualDuration}
        timingFunction={timingFunction}
        onEnter={onEnter}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        {(styles) => (
          <Box
            ref={ref}
            style={{
              ...styles,
              transitionDelay: `${delay}ms`,
              backfaceVisibility: 'hidden',
            }}
          >
            {children}
          </Box>
        )}
      </Transition>
    );
  }
);

FlipIn.displayName = 'FlipIn';

export default FlipIn;
